import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Check, Plus, MapPin, Loader2 } from "lucide-react";
import { FALLBACK_CATEGORIES, getCategoryIcon } from "../lib/categories";
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const WORK_ARRANGEMENTS = ["On-Site", "Hybrid", "Remote"];

export default function CompanyRoleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [skills, setSkills] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [location, setLocation] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [workArrangement, setWorkArrangement] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState("yearly");
  const [endClient, setEndClient] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const [locSuggestions, setLocSuggestions] = useState([]);
  const [locLoading, setLocLoading] = useState(false);
  const [showLocSuggestions, setShowLocSuggestions] = useState(false);
  const locDebounceRef = useRef(null);
  const locWrapperRef = useRef(null);

  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [titleLoading, setTitleLoading] = useState(false);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const titleDebounceRef = useRef(null);
  const titleWrapperRef = useRef(null);

  // Load role + categories in parallel
  useEffect(() => {
    Promise.all([
      api.get(`/company/roles/${id}`),
      api.get("/company/industries").catch(() => ({ data: [] })),
    ]).then(([roleRes, catRes]) => {
      const role = roleRes.data;
      const cats = catRes.data?.length ? catRes.data : FALLBACK_CATEGORIES;
      setCategories(cats);

      setTitle(role.title || "");
      setDesc(role.description || "");
      setSkills((role.required_skills || []).join(", "));
      setLocation(role.location || "");
      setEmploymentType(role.employment_type || "");
      setWorkArrangement(role.work_arrangement || "");
      setSalaryMin(role.salary_min != null ? String(role.salary_min) : "");
      setSalaryMax(role.salary_max != null ? String(role.salary_max) : "");
      setSalaryPeriod(role.salary_period || "yearly");
      setEndClient(role.end_client || "");

      // Handle category — if it matches the list use it, otherwise treat as "Other"
      if (cats.includes(role.category)) {
        setCategory(role.category);
      } else {
        setCategory("Other");
        setCustomCategory(role.category || "");
      }
    }).catch(() => {
      toast.error("Could not load role");
      navigate(`/company/roles/${id}`);
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleLocationInput = (val) => {
    setLocation(val);
    clearTimeout(locDebounceRef.current);
    if (val.trim().length < 2) { setLocSuggestions([]); setShowLocSuggestions(false); return; }
    setLocLoading(true);
    locDebounceRef.current = setTimeout(async () => {
      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(val)}&limit=6&lang=en&layer=city&layer=district&layer=locality&layer=county`;
        const res = await fetch(url);
        const json = await res.json();
        const seen = new Set();
        const items = (json.features || [])
          .map((f) => { const p = f.properties; return [p.name, p.state, p.country].filter(Boolean).join(", "); })
          .filter((label) => seen.has(label) ? false : seen.add(label));
        setLocSuggestions(items);
        setShowLocSuggestions(items.length > 0);
      } catch { setLocSuggestions([]); }
      finally { setLocLoading(false); }
    }, 300);
  };

  const pickLocation = (label) => {
    setLocation(label);
    setLocSuggestions([]);
    setShowLocSuggestions(false);
  };

  const handleTitleInput = (val) => {
    setTitle(val);
    clearTimeout(titleDebounceRef.current);
    if (val.trim().length < 2) { setTitleSuggestions([]); setShowTitleSuggestions(false); return; }
    setTitleLoading(true);
    titleDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/roles/title-search", { params: { q: val.trim() } });
        const items = (data || []).map((r) => r.name || r);
        setTitleSuggestions(items);
        setShowTitleSuggestions(items.length > 0);
      } catch { setTitleSuggestions([]); }
      finally { setTitleLoading(false); }
    }, 300);
  };

  const pickTitle = (name) => {
    setTitle(name);
    setTitleSuggestions([]);
    setShowTitleSuggestions(false);
  };

  const currentSkills = skills.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  const isAdded = (skill) => currentSkills.includes(skill.toLowerCase());

  const toggleSkill = (skill) => {
    const existing = skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (isAdded(skill)) {
      setSkills(existing.filter((s) => s.toLowerCase() !== skill.toLowerCase()).join(", "));
    } else {
      setSkills(existing.length ? existing.join(", ") + ", " + skill : skill);
    }
  };

  const suggestSkills = async () => {
    if (!desc.trim() || suggesting) return;
    setSuggesting(true);
    setSuggestions([]);
    try {
      const { data } = await api.post("/jobs/suggest-skills", { description: desc });
      setSuggestions(data.skills || []);
      if (!data.skills?.length) toast.info("No skills detected — try a more detailed description");
    } catch {
      toast.error("Could not fetch suggestions");
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const finalCategory = category === "Other" && customCategory.trim() ? customCategory.trim() : category;
    if (!finalCategory) { toast.error("Please select a category"); return; }
    setSubmitting(true);
    try {
      const required_skills = skills.split(",").map((s) => s.trim()).filter(Boolean);
      await api.patch(`/company/roles/${id}`, {
        title: title.trim(),
        description: desc.trim(),
        required_skills,
        category: finalCategory,
        location: location.trim() || null,
        employment_type: employmentType || null,
        work_arrangement: workArrangement || null,
        salary_min: salaryMin ? parseFloat(salaryMin) : null,
        salary_max: salaryMax ? parseFloat(salaryMax) : null,
        salary_period: (salaryMin || salaryMax) ? salaryPeriod : null,
        end_client: endClient.trim() || null,
      });
      toast.success("Role updated");
      navigate(`/company/roles/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground mb-3">
        <Link to="/company/roles" className="hover:text-foreground">ROLES</Link>
        <span>/</span>
        <Link to={`/company/roles/${id}`} className="hover:text-foreground truncate max-w-[200px]">{title || id}</Link>
        <span>/</span>
        <span>EDIT</span>
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mb-8">Edit role.</h1>

      <form onSubmit={submit} className="space-y-8 rounded-xl border border-border p-8 bg-white">

        {/* Title + End Client + Employment Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job Title *</Label>
              <div className="relative" ref={titleWrapperRef}>
                {titleLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none z-10" />}
                <Input
                  className="rounded-xl py-6"
                  required
                  value={title}
                  onChange={(e) => handleTitleInput(e.target.value)}
                  onFocus={() => titleSuggestions.length > 0 && setShowTitleSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTitleSuggestions(false), 150)}
                  placeholder="Senior Backend Engineer"
                  autoComplete="off"
                />
                {showTitleSuggestions && titleSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 rounded-xl border border-border bg-white shadow-lg mt-0.5">
                    {titleSuggestions.map((name) => (
                      <button key={name} type="button" onMouseDown={() => pickTitle(name)}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#0025D8]/5 hover:text-[#0025D8] transition-colors border-b border-border/50 last:border-0">
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">End Client</Label>
              <Input
                className="rounded-xl py-6"
                value={endClient}
                onChange={(e) => setEndClient(e.target.value)}
                placeholder="e.g. Royal Bank of Canada"
              />
              <p className="font-mono text-[10px] text-muted-foreground mt-1.5">Optional — the company you're placing this role with</p>
            </div>
          </div>
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Employment Type</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {EMPLOYMENT_TYPES.map((t) => (
                <button key={t} type="button" onClick={() => setEmploymentType(employmentType === t ? "" : t)}
                  className={`font-mono text-[10px] px-2.5 py-1 border transition-colors ${employmentType === t ? "bg-[#0F172A] text-white border-[#0A0A0A]" : "text-muted-foreground border-border hover:border-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Category */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-3 block">Category *</Label>
          <div className="flex flex-wrap gap-2">
            {[...categories, "Other"].map((cat) => {
              const Icon = getCategoryIcon(cat);
              return (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`inline-flex items-center gap-1.5 font-mono text-[11px] px-3 py-1.5 border font-semibold transition-all duration-150 ${
                    category === cat
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  <Icon className="w-3 h-3 flex-shrink-0" />
                  {cat}
                </button>
              );
            })}
          </div>
          {category === "Other" && (
            <div className="mt-3 space-y-1">
              <Input
                className="rounded-xl max-w-xs"
                placeholder="e.g. AgriTech, EdTech, CleanEnergy…"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
              <p className="font-mono text-[10px] text-[#F59E0B]">This will be sent to admin as a suggestion to add as an industry.</p>
            </div>
          )}
        </div>

        {/* Location */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Location</Label>
          <div className="relative max-w-sm" ref={locWrapperRef}>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              {locLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin pointer-events-none" />}
              <Input
                className="rounded-xl py-6 pl-9"
                value={location}
                onChange={(e) => handleLocationInput(e.target.value)}
                onFocus={() => locSuggestions.length > 0 && setShowLocSuggestions(true)}
                onBlur={() => setTimeout(() => setShowLocSuggestions(false), 150)}
                placeholder="Remote · New York, NY · London"
                autoComplete="off"
              />
            </div>
            {showLocSuggestions && locSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 rounded-xl border border-border bg-white shadow-lg mt-0.5">
                {locSuggestions.map((label) => (
                  <button key={label} type="button" onMouseDown={() => pickLocation(label)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#0025D8]/5 hover:text-[#0025D8] transition-colors flex items-center gap-2 border-b border-border/50 last:border-0">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Work Arrangement + Salary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Work Arrangement</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {WORK_ARRANGEMENTS.map((t) => (
                <button key={t} type="button" onClick={() => setWorkArrangement(workArrangement === t ? "" : t)}
                  className={`font-mono text-[10px] px-2.5 py-1 border transition-colors ${workArrangement === t ? "bg-[#0F172A] text-white border-[#0A0A0A]" : "text-muted-foreground border-border hover:border-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="uppercase tracking-wider text-xs font-semibold">Salary Range</Label>
              <div className="flex items-center rounded-xl border border-border overflow-hidden">
                {["yearly", "hourly"].map((p) => (
                  <button key={p} type="button" onClick={() => setSalaryPeriod(p)}
                    className={`font-mono text-[10px] px-2.5 py-1 transition-colors ${salaryPeriod === p ? "bg-[#0025D8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                    {p === "yearly" ? "/yr" : "/hr"}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input className="rounded-xl" type="number" min="0" value={salaryMin}
                onChange={(e) => setSalaryMin(e.target.value)} placeholder="Min" />
              <span className="text-muted-foreground text-sm flex-shrink-0">–</span>
              <Input className="rounded-xl" type="number" min="0" value={salaryMax}
                onChange={(e) => setSalaryMax(e.target.value)} placeholder="Max" />
            </div>
            <p className="font-mono text-[10px] text-muted-foreground mt-1.5">Leave blank to hide salary from applicants</p>
          </div>
        </div>

        {/* Description */}
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Job Description *</Label>
          <Textarea
            className="rounded-xl min-h-[220px]"
            required
            value={desc}
            onChange={(e) => { setDesc(e.target.value); setSuggestions([]); }}
            placeholder="What the role does, must-haves, nice-to-haves…"
          />
        </div>

        {/* Required Skills */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="uppercase tracking-wider text-xs font-semibold">Required Skills (comma separated)</Label>
            <button type="button" onClick={suggestSkills} disabled={!desc.trim() || suggesting}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border transition-all duration-150 ${
                desc.trim() && !suggesting
                  ? "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/50 hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED]"
                  : "bg-secondary text-muted-foreground border-border cursor-not-allowed opacity-50"
              }`}>
              <Sparkles className={`w-3.5 h-3.5 ${suggesting ? "animate-pulse" : ""}`} />
              {suggesting ? "Detecting…" : "Suggest from description"}
            </button>
          </div>
          <Input className="rounded-xl py-6" value={skills}
            onChange={(e) => setSkills(e.target.value)} placeholder="Python, FastAPI, MongoDB, AWS" />
          {suggestions.length > 0 && (
            <div className="mt-3">
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-2">SUGGESTED — click to add or remove</div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((skill) => {
                  const added = isAdded(skill);
                  return (
                    <button key={skill} type="button" onClick={() => toggleSkill(skill)}
                      className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border transition-all duration-150 ${
                        added ? "bg-[#0025D8] text-white border-[#0025D8]" : "bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/40 hover:bg-[#0025D8] hover:text-white hover:border-[#0025D8]"
                      }`}>
                      {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      {skill}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 pt-2">
          <Button type="submit" disabled={submitting || !category}
            className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] px-8 py-6 text-base">
            {submitting ? "Saving…" : "Save changes →"}
          </Button>
          <Link to={`/company/roles/${id}`} className="text-sm text-muted-foreground hover:text-foreground font-mono">
            Cancel
          </Link>
        </div>
      </form>
    </main>
  );
}
